import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { adminApi, EftPendingRow } from '../services/api';

/**
 * EFT Payments review queue (broker-AI only — route + nav gated via
 * IS_BROKER_ADMIN / SELF_SERVE_ONLY_PATHS). Lists outstanding EFT payment
 * requests: `pending` (invoice issued, no proof yet) and `awaiting_confirmation`
 * (a proof / bank line arrived + reconciled). An account manager verifies the
 * money reflected in the bank account, then one-click Confirm & Activate — the
 * single money->activation seam that activates the managed plan + marks the org
 * an EFT payer. Nothing here auto-activates; confirmation is always the AM's
 * deliberate action.
 */
function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '—';
  return `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function confidenceColor(confidence: string | null): 'success' | 'warning' | 'default' {
  if (confidence === 'exact') return 'success';
  if (confidence === 'ref-amount-mismatch' || confidence === 'ref-only') return 'warning';
  return 'default';
}

const EftPaymentsPage: React.FC = () => {
  const [rows, setRows] = useState<EftPendingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [confirmTarget, setConfirmTarget] = useState<EftPendingRow | null>(null);
  const [overrideAmount, setOverrideAmount] = useState('');
  // Cancel (write-off) — Trello #427. Separate target state from the confirm
  // dialog so the two can never be open against different rows at once.
  const [cancelTarget, setCancelTarget] = useState<EftPendingRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const fetchRows = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listEftPending();
      if (response.success) {
        // Awaiting-confirmation first (actionable), then pending (informational).
        setRows([...response.data.awaitingConfirmation, ...response.data.pending]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load EFT payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const openConfirm = (row: EftPendingRow) => {
    setConfirmTarget(row);
    // Pre-fill the amount with the OCR'd amount if present (may differ from
    // expected), else the expected amount — in RANDS for the AM to eyeball/edit.
    const cents = row.ocrAmountCents ?? row.expectedAmountCents;
    setOverrideAmount((cents / 100).toFixed(2));
  };

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    setActionLoading(true);
    setError(null);
    try {
      // Only send an override when the AM entered a POSITIVE amount that differs
      // from the expected — never a negative / zero / NaN (guards a fat-finger
      // from recording a bad monthly amount on the activated subscription).
      const enteredCents = Math.round(parseFloat(overrideAmount) * 100);
      const override =
        Number.isFinite(enteredCents) && enteredCents > 0 && enteredCents !== confirmTarget.expectedAmountCents
          ? enteredCents
          : undefined;
      const res = await adminApi.confirmEftPayment(confirmTarget.reference, override);
      setToast(
        `Activated ${res.data.tier} for ${confirmTarget.organizationId} (${formatCents(res.data.amountCents)}/month).`,
      );
      setConfirmTarget(null);
      await fetchRows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to confirm the EFT payment');
    } finally {
      setActionLoading(false);
    }
  };

  const openCancel = (row: EftPendingRow) => {
    setCancelTarget(row);
    setCancelReason('');
  };

  const handleCancel = async () => {
    if (!cancelTarget) return;
    setActionLoading(true);
    setError(null);
    try {
      const reason = cancelReason.trim();
      await adminApi.cancelEftRequest(cancelTarget.reference, reason || undefined);
      setToast(
        `Cancelled ${cancelTarget.reference} for ${cancelTarget.organizationId} — no further payment reminders will be sent for this invoice.`,
      );
      setCancelTarget(null);
      await fetchRows();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to cancel the EFT request');
    } finally {
      setActionLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'organizationId',
      headerName: 'Organisation',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.value}
        </Typography>
      ),
    },
    { field: 'tier', headerName: 'Plan', width: 100 },
    {
      field: 'expectedAmountCents',
      headerName: 'Expected',
      width: 130,
      valueFormatter: (value: number) => formatCents(value),
    },
    {
      field: 'ocrAmountCents',
      headerName: 'On proof',
      width: 130,
      valueFormatter: (value: number | null) => formatCents(value),
    },
    {
      field: 'confidence',
      headerName: 'Match',
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label={params.value} size="small" color={confidenceColor(params.value)} variant="outlined" />
        ) : (
          <Typography variant="caption" color="text.secondary">
            no proof yet
          </Typography>
        ),
    },
    { field: 'reference', headerName: 'Reference', width: 170 },
    {
      field: 'status',
      headerName: 'Status',
      width: 160,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value === 'awaiting_confirmation' ? 'awaiting confirm' : params.value}
          size="small"
          color={params.value === 'awaiting_confirmation' ? 'warning' : 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'proofUrl',
      headerName: 'Proof',
      width: 90,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => {
        const href = safeHref(params.value);
        return href ? (
          <Button size="small" href={href} target="_blank" rel="noopener noreferrer">
            View
          </Button>
        ) : (
          <Typography variant="caption" color="text.secondary">
            —
          </Typography>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 290,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => openConfirm(params.row as EftPendingRow)}
          >
            Confirm &amp; Activate
          </Button>
          {/*
            Labelled "Cancel invoice", not "Cancel" — every dialog on this page
            already has a "Cancel" button meaning "dismiss", and a destructive
            row action that shares that word is the one most likely to be
            clicked by accident.
          */}
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => openCancel(params.row as EftPendingRow)}
          >
            Cancel invoice
          </Button>
        </Box>
      ),
    },
  ];

  const term = searchTerm.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      !term ||
      r.organizationId?.toLowerCase().includes(term) ||
      r.reference?.toLowerCase().includes(term) ||
      r.tier?.toLowerCase().includes(term),
  );
  const awaitingCount = rows.filter((r) => r.status === 'awaiting_confirmation').length;
  // The amount field must be a positive number before Confirm is allowed.
  const parsedOverrideRands = parseFloat(overrideAmount);
  const overrideAmountInvalid = !Number.isFinite(parsedOverrideRands) || parsedOverrideRands <= 0;

  // Only bind a proof href we trust to be http(s) (defence-in-depth over the
  // backend-generated presigned URL) — never a javascript:/data: anchor sink.
  const safeHref = (url: unknown): string | undefined =>
    typeof url === 'string' && /^https?:\/\//i.test(url) ? url : undefined;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight={700}>
          EFT Payments
        </Typography>
        <Chip label={`${awaitingCount} awaiting confirm`} color="primary" variant="outlined" />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Outstanding EFT (bank transfer) subscription payments. When a proof of payment arrives it is
        reconciled against the expected amount and moved to <strong>awaiting confirm</strong> — verify
        the money reflected in the bank account, then <strong>Confirm &amp; Activate</strong> to
        activate the plan and mark the org an EFT payer. Confirming is always manual; nothing here
        activates a plan on its own. If the customer is not taking the subscription this month, use{' '}
        <strong>Cancel invoice</strong> — the invoice is written off and no further payment reminders
        go out for it. That covers the month only: next month is invoiced as usual, and the
        organisation&apos;s plan and access are left exactly as they are.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <TextField
            placeholder="Search by organisation, reference, or plan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ width: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <DataGrid
          rows={filtered}
          columns={columns}
          getRowId={(row) => row.reference}
          loading={loading}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          autoHeight
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            // Vertically centre EVERY cell — the default renderer centres plain
            // values, but a custom `renderCell` (Organisation Typography, the
            // Match/Status chips) top-aligns unless the cell itself is a
            // full-height flex box. This keeps the whole row on one baseline.
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
            '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
          }}
        />
      </Card>

      {/* Confirm & Activate dialog */}
      <Dialog open={!!confirmTarget} onClose={() => !actionLoading && setConfirmTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm &amp; Activate</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Activate the <strong>{confirmTarget?.tier}</strong> plan for{' '}
            <strong>{confirmTarget?.organizationId}</strong> (reference{' '}
            <strong>{confirmTarget?.reference}</strong>)? This writes an active managed subscription,
            marks the org an EFT payer, and records the monthly amount below — do this only once the
            payment has reflected in the bank account.
          </DialogContentText>
          <TextField
            label="Monthly amount to record (R)"
            value={overrideAmount}
            onChange={(e) => setOverrideAmount(e.target.value)}
            type="number"
            fullWidth
            disabled={actionLoading}
            error={overrideAmountInvalid}
            helperText={
              overrideAmountInvalid
                ? 'Enter a positive amount.'
                : `Expected ${formatCents(confirmTarget?.expectedAmountCents)}. Adjust only if the amount actually paid differs.`
            }
            inputProps={{ min: 0, step: '0.01' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="success"
            disabled={actionLoading || overrideAmountInvalid}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {actionLoading ? 'Activating...' : 'Confirm & Activate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel (write-off) dialog — Trello #427 */}
      <Dialog
        open={!!cancelTarget}
        onClose={() => !actionLoading && setCancelTarget(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Cancel this EFT invoice?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Write off <strong>{cancelTarget?.reference}</strong> for{' '}
            <strong>{cancelTarget?.organizationId}</strong> ({formatCents(cancelTarget?.expectedAmountCents)}).
            The invoice leaves this queue and the system stops sending payment reminders for it.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            This affects <strong>this month&apos;s invoice only</strong>. The organisation keeps its
            plan and its access, and it will be invoiced again next month as normal. To stop billing
            the customer altogether, change the organisation itself rather than cancelling here.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            fullWidth
            multiline
            minRows={2}
            disabled={actionLoading}
            inputProps={{ maxLength: 500 }}
            helperText="Recorded against the invoice so the month can be reconciled later."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)} disabled={actionLoading}>
            Keep invoice
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="error"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {actionLoading ? 'Cancelling...' : 'Cancel invoice'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)} sx={{ width: '100%' }}>
          {toast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EftPaymentsPage;
